import { HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot = false, children, ...props }, ref) => {
    const variants = {
      default: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600',
      success: 'bg-success-50 dark:bg-success-500/20 text-success-700 dark:text-success-500 border-success-200 dark:border-success-500/30',
      warning: 'bg-warning-50 dark:bg-warning-500/20 text-warning-700 dark:text-warning-500 border-warning-200 dark:border-warning-500/30',
      error: 'bg-error-50 dark:bg-error-500/20 text-error-700 dark:text-error-500 border-error-200 dark:border-error-500/30',
      info: 'bg-primary-50 dark:bg-primary-500/20 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-500/30',
      secondary: 'bg-accent-50 dark:bg-accent-500/20 text-accent-700 dark:text-accent-400 border-accent-200 dark:border-accent-500/30',
    };

    const sizes = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-xs',
      lg: 'px-3 py-1.5 text-sm',
    };

    const dotColors = {
      default: 'bg-gray-500',
      success: 'bg-success-500',
      warning: 'bg-warning-500',
      error: 'bg-error-500',
      info: 'bg-primary-500',
      secondary: 'bg-accent-500',
    };

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 font-medium rounded-full border',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span className={cn('w-1.5 h-1.5 rounded-full', dotColors[variant])} />
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };
